'use client';

import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef } from 'react';

import { getResponsiveAttributes } from '../utils';
import type { ResponsiveValue } from '../utils/types';
import styles from './text.component.module.scss';

type AliasColor =
  | 'brandColor'
  | 'white'
  | 'onSurfaceHighEmphasis'
  | 'onSurfaceMediumEmphasis'
  | 'onSurfaceLowEmphasis'
  | 'brandSoftMain'
  | 'danger'
  | 'interactiveInteractive'
  | 'accentColorAccent';
type Spacing = 4 | 8 | 10 | 12 | 16 | 20 | 24 | 32;
type Align = 'inherit' | 'left' | 'center' | 'right';
type TypographyToken =
  | 'heading-xs'
  | 'heading-s'
  | 'heading-m'
  | 'heading-l'
  | 'heading-xl'
  | 'heading-xxl'
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'heading4'
  | 'heading5'
  | 'control-m'
  | 'caption'
  | 'caption-s'
  | 'caption-m'
  | 'body-s'
  | 'body-m'
  | 'body1'
  | 'body2'
  | 'body3'
  | 'body4'
  | 'badge'
  | 'button'
  | 'label';
type Typography = TypographyToken | ResponsiveValue<TypographyToken>;

type TextSpanProps = {
  as?: 'span';
} & ComponentPropsWithoutRef<'span'>;
type TextDivProps = {
  as: 'div';
  align?: Align;
} & ComponentPropsWithoutRef<'div'>;
type TextPProps = {
  as: 'p';
  align?: Align;
} & ComponentPropsWithoutRef<'p'>;

type TextOwnProps = {
  /**
   * 文字色トークン
   */
  color?: AliasColor;
  /**
   * タイポグラフィートークン。指定しない場合はinheritします。
   */
  font?: Typography;

  /**
   * margin-top
   */
  mt?: Spacing;
  /**
   * margin-bottom
   */
  mb?: Spacing;
  children: ReactNode;
};

type TextProps = (TextSpanProps | TextDivProps | TextPProps) & TextOwnProps;

// MEMO:
// - HTMLDivElement/HTMLParagraphElementのalignプロパティは非推奨
//   - https://developer.mozilla.org/en-US/docs/Web/API/HTMLDivElement
//   - https://developer.mozilla.org/en-US/docs/Web/API/HTMLParagraphElement
// - HTMLDivElement/HTMLParagraphElement/HTMLSpanElementは上記プロパティを除くと、全てHTMLElementを継承しているだけになる
// - よって本コンポーネントは互換性を考慮してHTMLSpanElementを型として使用
//   - これによりラッパーコンポーネント側でforwardRefするときに、HTMLDivElement/HTMLParagraphElementでも型定義できる
export const Text = forwardRef<HTMLSpanElement, TextProps>((props, ref) => {
  const {
    as: Root = 'span',
    color,
    font,
    mt,
    mb,
    children,
    className,
    ...rest
  } = props;

  return (
    <Root
      data-color={color}
      // @ts-expect-error ref の型が異なるがrefとしては上記コメントにより実質同じ型なのでエラーを無視
      ref={ref}
      {...getResponsiveAttributes('font', font)}
      className={[className, styles.text].filter(Boolean).join(' ')}
      data-inline-block={Root === 'span' && (mt || mb) ? true : undefined}
      data-mb={mb}
      data-mt={mt}
      data-text-align={'align' in rest ? rest.align : undefined}
      {...rest}
    >
      {children}
    </Root>
  );
});

Text.displayName = 'Text';
